import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Scale, Building2, ArrowUpRight, Repeat } from 'lucide-react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { launchCorporateSuite } from '../../services/corporateApi';
import { rolesOfUser, Role, ROLE_LABELS } from '../../types/practiceArea';
import { cn } from '../../lib/utils';

/**
 * Header/sidebar control for the role-scoped workspace (design §2.3, roles-as-boundary).
 *
 * Roles are now the workspace boundary — the free practice-area lens dropdown is
 * gone. This control shows the current active workspace (read from the session
 * `nyayabodh_active_role` written by the login chooser) and offers a single
 * "Switch workspace…" action that re-runs the chooser IN PLACE: it clears the
 * session chosen-key + active_role and reloads, which re-mounts App →
 * PracticeAreaGate → the chooser (or auto-selects if the user has exactly one
 * in-app role). NO logout, no data loss.
 *
 * The switch action is only meaningful for lawyers with >1 in-app role (mirrors
 * PracticeAreaGate). For ≤1-role users we hide it — a re-choose would just
 * auto-select the same role. Corporate users still get the Corporate Suite
 * hand-off (a separate app reached via SSO), so the control never fully vanishes
 * for them.
 */
const PracticeAreaSwitcher: React.FC<{ collapsed?: boolean }> = ({ collapsed }) => {
  const profile = useSelector((s: RootState) => s.auth.user);
  const [open, setOpen] = useState(false);
  const [launching, setLaunching] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // In-app roles (corporate excluded — it's a separate app, not a lens).
  // Mirrors the PracticeAreaGate logic so "switchable" here == "chooser shown".
  const allRoles = rolesOfUser(profile?.practice_areas);
  const inAppRoles = [...allRoles].filter((r) => r !== 'corporate');
  const canSwitch = inAppRoles.length > 1;
  const isCorporate = allRoles.has('corporate');

  // Current active workspace label from the session (chosen at login).
  const activeRole = (() => {
    try {
      return sessionStorage.getItem('nyayabodh_active_role') as Role | null;
    } catch {
      return null;
    }
  })();
  const current = activeRole && ROLE_LABELS[activeRole] ? ROLE_LABELS[activeRole] : 'All areas';

  // In-place re-choose: clear the session chosen-key + active role and reload.
  // App re-mounts → PracticeAreaGate re-shows the chooser (or auto-selects for a
  // single-role user). NO logout — Firebase session + all data are untouched.
  const switchWorkspace = () => {
    try {
      sessionStorage.removeItem('nyayabodh_practice_area_chosen');
      sessionStorage.removeItem('nyayabodh_active_role');
    } catch {
      /* ignore storage errors — reload still re-runs the gate */
    }
    window.location.reload();
  };

  // Corporate is a separate app (corporate-web) — hand off via SSO.
  const launchCorporate = async () => {
    if (launching) return;
    setLaunching(true);
    try {
      await launchCorporateSuite();
      // Success navigates away.
    } catch (e) {
      console.error('Failed to open Corporate Suite:', e);
      setLaunching(false);
    }
  };

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Nothing to offer: single-role, non-corporate lawyer. Hide entirely.
  if (!canSwitch && !isCorporate) return null;

  if (collapsed) {
    return (
      <button
        onClick={() => setOpen((o) => !o)}
        title={`Workspace: ${current}`}
        className="w-full flex justify-center py-2 text-chamber-muted-dim hover:text-[#F4EEDF]"
      >
        <Scale className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div ref={ref} className="relative px-2">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={`Workspace: ${current}`}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="practice-area-switcher"
        className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-chamber-panel hover:bg-white/[0.04] border border-chamber-border text-sm text-chamber-muted transition-colors"
      >
        <span className="flex items-center gap-2 truncate">
          <Scale className="h-3.5 w-3.5 text-brass shrink-0" />
          <span className="truncate">{current}</span>
        </span>
        <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute left-2 right-2 mt-1 z-50 rounded-lg bg-chamber-card border border-chamber-border shadow-xl py-1">
          {canSwitch && (
            <button
              onClick={() => {
                setOpen(false);
                switchWorkspace();
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-chamber-muted hover:bg-white/[0.04] hover:text-[#F4EEDF]"
            >
              <Repeat className="h-3.5 w-3.5 text-brass shrink-0" />
              <span>Switch workspace…</span>
            </button>
          )}
          {/* Corporate is a separate app — launches via SSO, not a workspace role. */}
          {isCorporate && (
            <>
              {canSwitch && <div className="h-px bg-brass-hairline-faint my-1" />}
              <button
                onClick={() => {
                  setOpen(false);
                  void launchCorporate();
                }}
                disabled={launching}
                className="w-full flex items-center justify-between px-3 py-1.5 text-sm text-chamber-muted hover:bg-white/[0.04] hover:text-[#F4EEDF] disabled:opacity-60"
              >
                <span className="flex items-center gap-2">
                  <Building2 className="h-3.5 w-3.5 text-brass" />
                  {launching ? 'Opening…' : 'Corporate Suite'}
                </span>
                <ArrowUpRight className="h-3.5 w-3.5 text-chamber-muted-dim" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PracticeAreaSwitcher;
